<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
    error: {
        type: String,
        required: true,
    },
    errorCode: {
        type: String,
        default: null,
    },
    errorDetails: {
        type: Object,
        default: null,
    },
    departureLocation: {
        type: Object,
        default: null,
    },
    arrivalLocation: {
        type: Object,
        default: null,
    },
    selectedDate: {
        type: String,
        default: null,
    },
});

const getErrorMessage = () => {
    if (props.errorDetails?.description) {
        return props.errorDetails.description;
    }
    return props.error;
};

// Function to get translated error message based on error code
const getTranslatedError = () => {
    if (!props.errorCode) return { title: props.error, description: props.error };

    const errorKeyMap: Record<string, string> = {
        '126': 'errors.codeshare_flight',
        '913': 'errors.no_flights_found',
        '914': 'errors.invalid_search',
        '911': 'errors.system_error',
        '721': 'errors.too_many_results',
        'MGATE-001': 'errors.auth_failed',
        'MGATE-002': 'errors.access_denied',
        'MGATE-003': 'errors.rate_limit',
        'MGATE-004': 'errors.service_unavailable',
        '4926': 'errors.past_date_error',
        '4927': 'errors.flight_status_failed',
        '500': 'errors.flight_status_failed',
    };

    const errorKey = errorKeyMap[props.errorCode];
    if (errorKey) {
        return {
            title: t(`${errorKey}.title`),
            description: t(`${errorKey}.description`),
            additionalInfo: t(`${errorKey}.additional_info`, null),
        };
    }

    return { title: props.error, description: props.error };
};

// Function to get error icon based on error code
const getErrorIcon = () => {
    if (!props.errorCode) return 'mdi-magnify-remove';

    const iconMap: Record<string, string> = {
        '913': 'mdi-magnify-remove', // No flights found
        '914': 'mdi-alert-circle', // Invalid search
        '911': 'mdi-server-off', // System error
        '126': 'mdi-airplane', // Codeshare flight
        '721': 'mdi-database-search', // Too many results
        'MGATE-001': 'mdi-key-remove', // Auth failed
        'MGATE-002': 'mdi-lock', // Access denied
        'MGATE-003': 'mdi-timer-sand', // Rate limit
        'MGATE-004': 'mdi-server-off', // Service unavailable
        '4926': 'mdi-calendar-alert', // Past date error
        '4927': 'mdi-server-off', // Flight status failed
        '500': 'mdi-server-off', // Flight status failed
    };

    return iconMap[props.errorCode] || 'mdi-magnify-remove';
};

// Function to get error color based on error code
const getErrorColor = () => {
    if (!props.errorCode) return 'warning';

    const colorMap: Record<string, string> = {
        '913': 'warning', // No flights found - warning
        '914': 'error', // Invalid search - error
        '911': 'error', // System error - error
        '126': 'info', // Codeshare flight - info
        '721': 'warning', // Too many results - warning
        'MGATE-001': 'error', // Auth failed - error
        'MGATE-002': 'error', // Access denied - error
        'MGATE-003': 'warning', // Rate limit - warning
        'MGATE-004': 'error', // Service unavailable - error
        '4926': 'error', // Past date error - error
        '4927': 'error', // Flight status failed - error
        '500': 'error', // Flight status failed - error
    };

    return colorMap[props.errorCode] || 'warning';
};

// Format date for display
const formatDate = (dateString: string) => {
    if (!dateString) return '';

    try {
        const date = new Date(dateString);
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        };
        return date.toLocaleDateString('en-US', options);
    } catch (e) {
        return dateString;
    }
};

// Get route information for display
const getRouteInfo = () => {
    if (!props.departureLocation || !props.arrivalLocation) return '';

    const departure = `${props.departureLocation.city}(${props.departureLocation.code})`;
    const arrival = `${props.arrivalLocation.city}(${props.arrivalLocation.code})`;
    const date = formatDate(props.selectedDate);

    return `${departure} to ${arrival} ${date}`;
};
</script>

<template>
    <v-container>
        <div class="fs-error-container">
            <div class="fs-error-card">
                <!-- Error Icon -->
                <!-- <div class="fs-error-icon-container">
                    <v-icon :icon="getErrorIcon()" size="64" :color="getErrorColor()" class="fs-error-icon"></v-icon>
                </div> -->
                <h2 class="fs-filter-error-title">{{ t('flight_status.search_results') }}</h2>
                <!-- Error Message -->
                <div class="fs-error-message">
                    <figure class="fs-error-image">
                        <img src="/images/search-img.svg" alt="Image" />
                    </figure>
                    <h2 class="fs-error-title">
                        {{ getTranslatedError().title }}
                    </h2>

                    <!-- Route Information -->
                    <!-- <div v-if="getRouteInfo()" class="fs-route-info text-body-1 mb-3">
                        {{ getRouteInfo() }}
                    </div> -->

                    <!-- Error Description -->
                    <div class="fs-error-description">
                        {{ getTranslatedError().description }}
                    </div>

                    <!-- Additional Info for specific error codes -->
                    <!-- <div v-if="getTranslatedError().additionalInfo" class="fs-additional-info text-body-2 text-grey-darken-1">
                        {{ getTranslatedError().additionalInfo }}
                    </div> -->
                </div>

                <!-- Timestamp -->
                <!-- <div class="fs-timestamp text-caption text-grey-darken-1">
                    Last Updated: {{ new Date().toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                    }) }}, {{ new Date().toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                    }) }}
                </div> -->
            </div>

            <!-- Help Section -->
            <!-- <div class="fs-help-section mt-6">
                <v-expansion-panels variant="accordion">
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <v-icon icon="mdi-help-circle" class="me-2"></v-icon>
                            {{ t('errors.help.title') }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div class="fs-contact-info">
                                <p class="text-body-2 mb-2">
                                    {{ t('errors.help.description') }}
                                </p>
                                <ul class="text-body-2">
                                    <li>{{ t('errors.help.phone') }}: +40 21 204 6464</li>
                                    <li>{{t('email')}}: customer.service@tarom.ro</li>
                                    <li>{{ t('errors.help.available') }}</li>
                                </ul>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                    
                    <v-expansion-panel>
                        <v-expansion-panel-title>
                            <v-icon icon="mdi-shield-check" class="me-2"></v-icon>
                            {{ t('errors.legal.title') }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <div class="fs-legal-info">
                                <p class="text-body-2">
                                    {{ t('errors.legal.description') }}
                                </p>
                            </div>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </div> -->
        </div>
    </v-container>
</template>
